import { useEffect } from "react";
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormReset,
  type UseFormSetValue,
  type UseFormWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ActiveCompanies, Site } from "../../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { applyHighlightingToObjectFormFieldData } from "../../../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { DueDateFields } from "../../../../BuildingBlocks/Forms/FormFields/GroupedFields/DueDateFields/DueDateFields";
import { isFieldError } from "../../../../BuildingBlocks/Forms/MantineFormFields/HelperText/utils/isFieldError";
import {
  MeasuringPointPriceOption,
  PaymentIntervalAndBillingMethod,
  PaymentMethod,
  VATExemptionReason,
  type Contract
} from "../../../DeliveryOverview.types";
import { ChangeablePricesField } from "../../SharedFields/ChangeablePricesField";
import type { ContractFormOptions } from "../ContractModal.types";
import {
  CONTRACT_FORM_FIELD_DATA,
  INITIAL_INSTALLMENT
} from "./ContractForm.constants";
import "./ContractForm.scss";
import { AutomaticExtensionFields } from "./GroupedFields/AutomaticExtensionFields";
import { InitialInstallmentFields } from "./GroupedFields/InitialInstallmentFields";
import { SupplierAndSupplied } from "./GroupedFields/SupplierAndSupplied";
import { TemplateFields } from "./GroupedFields/TemplateFields";

interface ContractFormFieldsProps {
  control: Control<Contract>;
  errors: FieldErrors<Contract>;
  watch: UseFormWatch<Contract>;
  setValue: UseFormSetValue<Contract>;
  reset: UseFormReset<Contract>;
  options: ContractFormOptions;
  activeCompanies?: ActiveCompanies;
  contract?: Contract;
  templateContracts?: Array<Contract>;
  withTemplate?: boolean;
  sites?: Array<Site>;
  missingFields: Array<string>;
  shouldFieldBeHighlighted: (
    fieldName: string,
    relevantFormValues: Record<string, unknown>
  ) => boolean;
}

function ContractFormFields({
  control,
  errors,
  watch,
  setValue,
  reset,
  options,
  activeCompanies,
  contract,
  templateContracts,
  withTemplate,
  sites,
  missingFields,
  shouldFieldBeHighlighted
}: ContractFormFieldsProps) {
  const { t } = useTranslation();

  const { formFieldData } = applyHighlightingToObjectFormFieldData(
    CONTRACT_FORM_FIELD_DATA,
    watch,
    contract
      ? {
          wizardObject: contract,
          fieldsThatCouldBeMissing: missingFields,
          shouldFieldBeHighlighted: shouldFieldBeHighlighted
        }
      : undefined
  );

  const watchCreationTemplate = watch("creation_template");
  const watchCreationTemplateSite = watch("creation_template_site");
  const watchBillingMethodAndPeriod = watch("billing_method_and_period");
  const templatesFieldsAreFilled =
    watchCreationTemplate && watchCreationTemplateSite;
  const showTemplateFields =
    withTemplate && templateContracts && sites && !templatesFieldsAreFilled;
  const isNewContract = !contract;
  const watchSupplied = watch("supplied");
  const watchSupplier = watch("supplier");
  const watchBegin = watch("begin");
  const showAutomaticExtension = watch("automatic_extension") === true;
  const showMonthlyInvoiceDocumentsForAdvancePayment =
    watchBillingMethodAndPeriod ===
    PaymentIntervalAndBillingMethod.MonthlyAdvancePaymentsAndAnnualFinalInvoice;
  const showPreviousElectricityConsumption =
    watch("monthly_invoice_documents_for_advance_payment") === true;
  const showMandateReference =
    watch("payment_method") === PaymentMethod.DirectDebit;
  const watchAddPriceMeasuringPoint = watch("add_price_measuring_point");
  const showPriceMeasuringPoint =
    watchAddPriceMeasuringPoint &&
    watchAddPriceMeasuringPoint !== MeasuringPointPriceOption.None;
  const showAddBasePrice = watch("add_base_price");
  const watchIsVatIncluded = watch("is_vat_included");
  const showVatExemptionReason = watchIsVatIncluded === false;
  const showCustomVatExemptionReason =
    showVatExemptionReason &&
    watch("vat_exemption_reason") === VATExemptionReason.Custom;

  useEffect(() => {
    if (
      watchCreationTemplate &&
      templateContracts &&
      watchCreationTemplateSite
    ) {
      const valuesFromTemplate = templateContracts?.find(
        (contract) => contract.id === watchCreationTemplate
      );

      if (valuesFromTemplate) {
        reset({
          ...valuesFromTemplate,
          id: undefined,
          is_template: false,
          name: undefined,
          supplier: undefined,
          supplied: undefined,
          creation_template: watchCreationTemplate,
          creation_template_site: watchCreationTemplateSite
        });
      } else {
        reset();
      }
    }
  }, [
    watchCreationTemplate,
    watchCreationTemplateSite,
    templateContracts,
    setValue,
    reset
  ]);

  useEffect(() => {
    if (!showAutomaticExtension) {
      setValue("extension_duration_value", null);
      setValue("extension_duration_unit", null);
      setValue("cancelation_deadline_value", null);
      setValue("cancelation_deadline_unit", null);
      setValue("cancelation_reference", null);
    }
  }, [showAutomaticExtension, setValue]);

  useEffect(() => {
    if (!showMonthlyInvoiceDocumentsForAdvancePayment) {
      setValue("monthly_invoice_documents_for_advance_payment", false);
      setValue("previous_electricity_consumption", false);
    }
  }, [showMonthlyInvoiceDocumentsForAdvancePayment, setValue]);

  useEffect(() => {
    if (!showPreviousElectricityConsumption) {
      setValue("previous_electricity_consumption", false);
    }
  }, [showPreviousElectricityConsumption, setValue]);

  useEffect(() => {
    if (!showMandateReference) {
      setValue("mandate_reference", undefined);
    }
  }, [showMandateReference, setValue]);

  useEffect(() => {
    if (!showVatExemptionReason) {
      setValue("vat_exemption_reason", null);
      setValue("custom_vat_exemption_reason", null);
    }
  }, [showVatExemptionReason, setValue]);

  useEffect(() => {
    if (!showCustomVatExemptionReason) {
      setValue("custom_vat_exemption_reason", null);
    }
  }, [showCustomVatExemptionReason, setValue]);

  function getPriceMeasuringPointLabel(priceOption: MeasuringPointPriceOption) {
    if (priceOption === MeasuringPointPriceOption.Total) {
      return "Monatliche Messkosten (netto) in Summe";
    } else if (priceOption === MeasuringPointPriceOption.PerMeter) {
      return "Monatliche Messkosten (netto) je Zähler";
    }

    return "Monatliche Messkosten (netto, ggf. je Zähler)";
  }

  if (showTemplateFields) {
    return (
      <TemplateFields
        control={control}
        errors={errors}
        sites={sites}
        templateContracts={templateContracts}
      />
    );
  }

  return (
    <>
      <FormFieldController
        control={control}
        data={formFieldData.name}
        error={errors.name}
      />

      <FormFieldController
        control={control}
        data={formFieldData.product_name}
        error={errors.product_name}
        rules={{
          maxLength: {
            value: 50,
            message: t("errors.MaxLength", { maxLength: 50 })
          }
        }}
      />

      <SupplierAndSupplied
        activeCompanies={activeCompanies}
        control={control}
        errors={errors}
        selectedSite={watchCreationTemplateSite ?? undefined}
        supplied={watchSupplied}
        suppliedChoices={options.supplied}
        supplier={watchSupplier}
        supplierChoices={options.supplier}
      />

      <FormFieldController
        control={control}
        data={formFieldData.begin}
        error={errors.begin}
      />

      <FormFieldController
        control={control}
        data={formFieldData.minimum_term}
        error={errors.minimum_term}
      />

      <FormFieldController
        control={control}
        data={formFieldData.automatic_extension}
        error={errors.automatic_extension}
      />

      {showAutomaticExtension && (
        <AutomaticExtensionFields control={control} errors={errors} />
      )}

      <FormFieldController
        control={control}
        data={formFieldData.end}
        error={errors.end}
        rules={{
          validate: (endValue, formValues) => {
            if (endValue && formValues.begin) {
              const beginDateTime = backendDateOrDateTimeToLuxonDateTime(
                formValues.begin
              );
              const endDateTime =
                backendDateOrDateTimeToLuxonDateTime(endValue);

              if (endDateTime < beginDateTime) {
                return t("errors.ContractForm.EndBeforeBegin");
              }
            }

            return true;
          }
        }}
      />

      <FormFieldController
        control={control}
        data={formFieldData.billing_method_and_period}
        error={errors.billing_method_and_period}
      />

      {isNewContract &&
        watchBegin &&
        showMonthlyInvoiceDocumentsForAdvancePayment && (
          <Controller
            control={control}
            name="installment_configurations"
            render={({ field }) => {
              return (
                <InitialInstallmentFields
                  installment={
                    field.value
                      ? field.value[0]
                      : INITIAL_INSTALLMENT(
                          backendDateOrDateTimeToLuxonDateTime(watchBegin).year
                        )
                  }
                  onChange={(installment) => field.onChange([installment])}
                />
              );
            }}
          />
        )}

      {showMonthlyInvoiceDocumentsForAdvancePayment && (
        <FormFieldController
          control={control}
          data={formFieldData.monthly_invoice_documents_for_advance_payment}
          error={errors.monthly_invoice_documents_for_advance_payment}
        />
      )}

      {showPreviousElectricityConsumption && (
        <FormFieldController
          control={control}
          data={CONTRACT_FORM_FIELD_DATA.previous_electricity_consumption}
          error={errors.previous_electricity_consumption}
        />
      )}

      <FormFieldController
        control={control}
        data={formFieldData.payment_method}
        error={errors.payment_method}
      />

      <DueDateFields
        control={control}
        errors={errors}
        formFieldData={formFieldData}
        unitFieldName="due_date_unit"
        valueFieldName="due_date_value"
      />

      {showMandateReference && (
        <FormFieldController
          control={control}
          data={formFieldData.mandate_reference}
          error={errors.mandate_reference}
          rules={{
            maxLength: {
              value: 50,
              message: t("errors.MaxLength", { maxLength: 50 })
            }
          }}
        />
      )}

      <FormFieldController
        control={control}
        data={formFieldData.allowance_send_invoice_via_mail}
        error={errors.allowance_send_invoice_via_mail}
      />

      <FormFieldController
        control={control}
        data={formFieldData.automatic_send_invoice_active}
        error={errors.automatic_send_invoice_active}
      />

      <FormFieldController
        control={control}
        data={formFieldData.add_price_measuring_point}
        error={errors.add_price_measuring_point}
      />

      {showPriceMeasuringPoint && (
        <ChangeablePricesField
          className="MeasuringPointPrices"
          control={control}
          error={
            isFieldError(errors.measuring_point_prices)
              ? errors.measuring_point_prices
              : undefined
          }
          label={getPriceMeasuringPointLabel(watchAddPriceMeasuringPoint)}
          name="measuring_point_prices"
          unit="EUR/Monat"
        />
      )}

      <FormFieldController
        control={control}
        data={formFieldData.add_base_price}
        error={errors.add_base_price}
      />

      {showAddBasePrice && (
        <ChangeablePricesField
          className="BasePrices"
          control={control}
          error={
            isFieldError(errors.base_prices) ? errors.base_prices : undefined
          }
          label={"Monatlicher Grundpreis (netto)"}
          name="base_prices"
          unit="EUR/Monat"
        />
      )}

      <FormFieldController
        control={control}
        data={formFieldData.deposit}
        error={errors.deposit}
        rules={{
          maxLength: {
            value: 50,
            message: t("errors.MaxLength", { maxLength: 50 })
          },
          min: {
            value: 0,
            message: t("errors.ContractForm.DepositMin0", { maxLength: 0 })
          }
        }}
      />

      <FormFieldController
        control={control}
        data={formFieldData.is_vat_included}
        error={errors.is_vat_included}
      />

      {showVatExemptionReason && (
        <FormFieldController
          control={control}
          data={formFieldData.vat_exemption_reason}
          error={errors.vat_exemption_reason}
        />
      )}

      {showCustomVatExemptionReason && (
        <FormFieldController
          control={control}
          data={formFieldData.custom_vat_exemption_reason}
          error={errors.custom_vat_exemption_reason}
        />
      )}
    </>
  );
}

export { ContractFormFields, ContractFormFieldsProps };
